import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiDownload, FiTarget, FiZap, FiEdit3, FiUsers, FiArrowRight, FiCheck, FiClock, FiTrendingUp, FiMessageCircle, FiFileText, FiTag } = FiIcons;

function App() {
  const handleCTAClick = () => {
    // Redirect to purchase page
    window.open('https://www.keyfutureskills.com/?add-to-cart=3442', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight mb-6">
                Sales AI Toolkit:
                <span className="block text-blue-300">ChatGPT Prompts</span>
                <span className="block text-2xl lg:text-3xl font-semibold text-blue-200">for Sales Teams</span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-blue-100 mb-8 leading-relaxed">
                AI Prospecting, LinkedIn Outreach & CRM Logging | 
                <span className="block font-semibold text-yellow-300">Instant Digital Download for SDRs & AEs</span>
              </p>
              
              <motion.button style={{
      color: 'rgb(255, 255, 255)'
    }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCTAClick}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-4 px-8 rounded-xl text-lg shadow-2xl transition-all duration-300"
              >
                <SafeIcon icon={FiDownload} className="text-xl" />Get Your Toolkit Now<SafeIcon icon={FiArrowRight} className="text-xl" />
              </motion.button>
              
              <div className="mt-4 space-y-2">
                <p className="text-sm text-blue-200">
                  ⚡ Instant PDF Download • 32 Pages • 100+ Prompts
                </p>
                <div className="flex items-center gap-2 text-white">
                  <span className="text-2xl font-bold text-yellow-300">$9.97</span>
                  <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold">25% OFF with code: ALL25</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center items-center"
            >
              <img
                src="https://digitalatips.se/wp-content/uploads/2025/10/Sales-AI-Toolkit_cover.jpg"
                alt="Sales AI Toolkit Ebook Cover"
                className="rounded-2xl shadow-2xl max-w-sm w-full transform rotate-3 hover:rotate-0 transition-transform duration-500"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-8">
              Stop wasting <span className="text-red-600">60% of your day</span> on admin 
              <span className="block text-3xl lg:text-4xl text-blue-700 mt-2">and start closing more deals.</span>
            </h2>
            
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 lg:p-12">
              <div className="flex items-start gap-4 mb-6">
                <SafeIcon icon={FiClock} className="text-3xl text-red-500 mt-1 flex-shrink-0" />
                <div className="text-left">
                  <p className="text-lg lg:text-xl text-gray-700 leading-relaxed">
                    Are you a <strong>Sales Rep (SDR/AE)</strong> or <strong>entrepreneur</strong> buried in manual prospect research, 
                    struggling to personalize hundreds of LinkedIn messages, and dreading the 'CRM note logging' at the end of every day?
                  </p>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-red-100 to-orange-100 rounded-xl p-6 mb-6">
                <p className="text-lg text-gray-800 leading-relaxed">
                  <strong>Studies show</strong> this relentless, non-revenue-generating grind eats up over <strong className="text-red-600">60% of a sales professional's precious time.</strong>
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-red-50 rounded-xl">
                  <SafeIcon icon={FiMessageCircle} className="text-2xl text-red-500 mx-auto mb-2" />
                  <p className="font-semibold text-gray-800">Generic outreach that gets ignored</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-xl">
                  <SafeIcon icon={FiUsers} className="text-2xl text-orange-500 mx-auto mb-2" />
                  <p className="font-semibold text-gray-800">Incomplete prospect profiles</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-xl">
                  <SafeIcon icon={FiFileText} className="text-2xl text-yellow-600 mx-auto mb-2" />
                  <p className="font-semibold text-gray-800">Outdated CRM data that kills follow-ups</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-8">
              It's time to work <span className="text-green-600">smarter</span>, not harder.
            </h2>
            
            <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl p-8 lg:p-12">
              <div className="mb-8">
                <h3 className="text-2xl lg:text-3xl font-bold text-blue-700 mb-4">
                  Welcome to the Sales AI Toolkit: Your Done-for-You ChatGPT Prompt Library
                </h3>
                <p className="text-lg lg:text-xl text-gray-700 leading-relaxed">
                  This isn't just another theoretical guide; it's your <strong>secret weapon</strong> to automate your most time-consuming tasks. 
                  Reclaim those invaluable hours and redirect your energy toward what truly matters: 
                  <strong className="text-green-600"> building rapport and closing more deals.</strong>
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl p-6">
                <p className="text-xl font-semibold text-gray-800 mb-2">
                  This 32-page, copy-and-paste prompt library is your new AI-powered workflow.
                </p>
                <div className="flex flex-wrap justify-center gap-4 mt-4">
                  <span className="bg-blue-200 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">32 Pages</span>
                  <span className="bg-green-200 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">100+ Prompts</span>
                  <span className="bg-purple-200 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold">Copy & Paste Ready</span>
                  <span className="bg-orange-200 text-orange-800 px-3 py-1 rounded-full text-sm font-semibold">Instant Download</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              ✨ WHAT'S INSIDE? ✨
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              This toolkit is packed with hundreds of fill-in-the-blank prompts organized into three critical sections:
            </p>
          </motion.div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Section 1 */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="bg-white rounded-2xl shadow-2xl p-8 hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="text-center mb-6">
                <SafeIcon icon={FiZap} className="text-4xl text-yellow-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  ⚡ SECTION 1: AI Prompts for Lightning-Fast Prospect Research
                </h3>
                <p className="text-gray-600">
                  Stop sifting through websites for hours. Go from a company name to a comprehensive, actionable brief in minutes.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span className="text-gray-700">Company & Market Analysis</span>
                </div>
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span className="text-gray-700">Pain Point Discovery</span>
                </div>
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span className="text-gray-700">Competitor Research</span>
                </div>
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span className="text-gray-700">Persona & Contact Research</span>
                </div>
              </div>
            </motion.div>

            {/* Section 2 */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl p-8 hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="text-center mb-6">
                <SafeIcon icon={FiTarget} className="text-4xl text-blue-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  🎯 SECTION 2: AI Prompts for Effective LinkedIn & Cold Outreach
                </h3>
                <p className="text-gray-600">
                  Stop sending generic outreach that gets deleted. Craft highly personalized, value-driven messages that demand a response.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span className="text-gray-700">Connection Requests</span>
                </div>
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span className="text-gray-700">Cold Email (First Touch)</span>
                </div>
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span className="text-gray-700">Personalized 'Icebreakers'</span>
                </div>
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span className="text-gray-700">Follow-Up Messages</span>
                </div>
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span className="text-gray-700">Handling Objections</span>
                </div>
              </div>
            </motion.div>

            {/* Section 3 */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="text-center mb-6">
                <SafeIcon icon={FiEdit3} className="text-4xl text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  ✍️ SECTION 3: AI Prompts for Zero-Effort CRM Note Logging
                </h3>
                <p className="text-gray-600">
                  Stop the drudgery of admin. Turn messy call notes or transcripts into perfectly structured, insightful CRM entries with a simple copy-paste.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span className="text-gray-700">Call Summaries</span>
                </div>
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span className="text-gray-700">Creating Action Items</span>
                </div>
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-500" />
                  <span className="text-gray-700">Updating Opportunity Stages</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              🚀 HOW IT WORKS <span className="text-green-600">(IT'S EASY!)</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              You don't need to be an AI expert. This toolkit is built on a simple three-step process:
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-center"
            >
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">COPY</h3>
              <p className="text-lg text-gray-600">
                Grab a prompt from the PDF.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center"
            >
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">FILL</h3>
              <p className="text-lg text-gray-600">
                Replace the [brackets] with your prospect's specific info (e.g.,[Company Name],[Job Title]).
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-center"
            >
              <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">PASTE</h3>
              <p className="text-lg text-gray-600">
                Paste it into ChatGPT,Gemini,or any AI tool and get a high-quality,actionable response in seconds.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-20 bg-gradient-to-br from-purple-100 to-blue-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-12">
              📈 WHO IS THIS FOR?
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                <SafeIcon icon={FiUsers} className="text-3xl text-blue-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Sales Development Reps (SDRs)</h3>
              </div>
              
              <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                <SafeIcon icon={FiTarget} className="text-3xl text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Account Executives (AEs)</h3>
              </div>
              
              <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                <SafeIcon icon={FiTrendingUp} className="text-3xl text-purple-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Entrepreneurs & Small Business Owners</h3>
              </div>
              
              <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                <SafeIcon icon={FiZap} className="text-3xl text-orange-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Any sales professional who wants to hit their target</h3>
              </div>
            </div>
            
            <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl p-8 text-white">
              <p className="text-xl lg:text-2xl font-semibold">
                Any sales professional who wants to hit their target,reclaim their time,and focus on what they do best: 
                <span className="block text-yellow-300 text-2xl lg:text-3xl mt-2">CLOSING.</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8">
              Stop <span className="text-red-400">"selling"</span> and start <span className="text-green-400">closing.</span>
            </h2>
            
            <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12 mb-12">
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6">
                📂 YOU WILL RECEIVE:
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-6">
                  <SafeIcon icon={FiFileText} className="text-3xl text-blue-600 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-gray-900 mb-2">1x Instant Download PDF File</h4>
                  <p className="text-gray-600">(32 Pages)</p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-6">
                  <SafeIcon icon={FiDownload} className="text-3xl text-green-600 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Instant Digital Delivery</h4>
                  <p className="text-gray-600">No physical item will be shipped</p>
                </div>
              </div>
              
              <div className="mb-8">
                <div className="flex items-center justify-center gap-4 mb-4">
                  <SafeIcon icon={FiTag} className="text-2xl text-orange-500" />
                  <span className="text-3xl font-bold text-gray-900">Only $9.97</span>
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">25% OFF with code: ALL25</span>
                </div>
                <p className="text-sm text-gray-600">
                  Use coupon code <strong className="text-green-600">ALL25</strong> at checkout for 25% off!
                </p>
              </div>
              
              <p className="text-sm text-gray-500 mb-8">
                (This is a digital product. No physical item will be shipped.)
              </p>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCTAClick}
                className="inline-flex items-center gap-4 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-6 px-12 rounded-2xl text-xl shadow-2xl transition-all duration-300"
              >
                👉 Get your toolkit now and supercharge your sales workflow today!
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">
            © 2024 Sales AI Toolkit. All rights reserved. | Digital Product - Instant Download
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;